<script setup>
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { PERIOD_FILTER_OPTIONS } from 'dashboard/composables/useKanbanBoard';
import { usePopoverTrigger } from '../composables/usePopoverTrigger';

// PRD T3.4 secao 3: "período de atividade tem default de 30 dias e ele é
// visível... a pastilha do período fica sempre presente e removível;
// removê-la avisa que a consulta vai ficar lenta." O contrato real da API
// (board_controller.rb#last_activity_after) sempre aplica um piso — não há
// forma de pedir "sem período" — então "remover" aqui é ALARGAR para a
// maior janela suportada (365 dias) em vez de um valor vazio que a API não
// aceita; a pastilha permanece visível com o valor efetivo em todo caso,
// que é o que sustenta a garantia central do PRD ("filtro que a pessoa não
// vê é mentira sobre o que ela está olhando").
defineProps({
  modelValue: { type: Number, required: true },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const {
  isOpen,
  triggerRef,
  toggle: toggleOpen,
  close,
  closeAndFocusTrigger,
} = usePopoverTrigger();

// Mesmo padrão do switcher de funil: escolher desmonta o botão da opção, e
// sem devolver o foco ao gatilho ele cai em BODY.
const select = days => {
  emit('update:modelValue', days);
  closeAndFocusTrigger();
};
</script>

<template>
  <OnClickOutside @trigger="close">
    <div class="relative">
      <!--
        BLOQUEADOR 2 (revisor-frontend, T3.4, 1.4.3): a variante `blue faded`
        do NextButton usa text-n-blue-11 (#086de0), que dá 4,09:1 sobre o bg
        renderizado (bg-n-brand/10 ~= #e2ebf7) no tema claro — abaixo de
        4.5:1, e este é o único filtro que o PRD exige sempre visível. No
        escuro já passava (7,19), então o override abaixo é só de tema
        claro; blue-12 é reativo a tema (--blue-12 muda de valor no bloco
        .dark de _next-colors.scss), logo continua correto lá (mais claro
        ainda, contraste sobe). !text-n-blue-12 (10,53:1 medido) segue o
        mesmo padrão de override já usado em MultiselectDropdown.vue
        (`class="w-full !px-2"` no mesmo componente Button) — sem editar o
        componente compartilhado, que mudaria todo botão blue/faded do app.
      -->
      <NextButton
        ref="triggerRef"
        blue
        faded
        xs
        trailing-icon
        icon="i-lucide-calendar-clock"
        class="!text-n-blue-12"
        :label="t('KANBAN.BOARD.PERIOD.OPTION_DAYS', { days: modelValue })"
        aria-haspopup="true"
        :aria-expanded="isOpen"
        @click="toggleOpen"
      />
      <div
        v-if="isOpen"
        class="absolute z-30 mt-1 w-56 rounded-lg border border-n-weak bg-n-solid-2 shadow-lg p-2"
        @keyup.esc="closeAndFocusTrigger"
      >
        <p class="m-0 mb-2 px-1 text-xs text-n-slate-11">
          {{ t('KANBAN.BOARD.PERIOD.DESCRIPTION') }}
        </p>
        <ul class="flex flex-col gap-0.5 m-0 p-0 list-none">
          <li v-for="days in PERIOD_FILTER_OPTIONS" :key="days">
            <button
              type="button"
              class="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm text-left hover:bg-n-alpha-2"
              :class="
                days === modelValue
                  ? 'text-n-slate-12 font-medium'
                  : 'text-n-slate-11'
              "
              @click="select(days)"
            >
              {{
                days === 365
                  ? t('KANBAN.BOARD.PERIOD.OPTION_YEAR')
                  : t('KANBAN.BOARD.PERIOD.OPTION_DAYS', { days })
              }}
              <span
                v-if="days === modelValue"
                class="i-lucide-check size-3.5"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </div>
    </div>
  </OnClickOutside>
</template>
